import {
  BILLING_HISTORY_QUERY_PARAM,
  IS_APPLE_PAY,
  IS_GOOGLE_PAY,
  PROMO_CODE_QUERY_PARAM,
} from "features/settings/common/constants.ts";
import { useUser } from "@shared/utils/hooks/misc.ts";
import { useHistory } from "react-router-dom";
import { useGeneralPaymentErrors } from "features/settings/common/hooks.tsx";
import { useContext, useState } from "react";
import { APPLE_PROMO_CODE_FEATURE_FLAG } from "common/constants.ts";
import { openMessage } from "utils/misc.tsx";
import {
  getProductId,
  getPromoCodeRoundedDiscount,
} from "@shared/features/settings/subscription/utils.ts";
import { getEnvironmentType } from "app/shared/utils.ts";
import { initiatePayment } from "services/webview/webViewBridgeUtils.ts";
import { SubscriptionPaymentMethodType } from "@shared/types";
import { getIsDifferentPaymentMethod } from "features/settings/Subscription/utils.tsx";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context.tsx";

interface useWebViewCheckoutProps {
  loadingPaymentLink?: boolean;
}

const useWebViewCheckout = ({
  loadingPaymentLink,
}: useWebViewCheckoutProps = {}) => {
  const { plan, plans, duration, promoCode, promoCodePrices, isUpgrade } =
    useContext(BuySubscriptionContext)!;
  const { user } = useUser();
  const history = useHistory();
  const warningMessage = useGeneralPaymentErrors({ plan });

  const [isLoading, setIsLoading] = useState(false);
  const [appleOfferCodeModalOpen, setAppleOfferCodeModalOpen] = useState(false);
  const [cancelLastSubscriptionModalOpen, setCancelLastSubscriptionModalOpen] =
    useState(false);

  const loading = loadingPaymentLink || isLoading;
  const hidePromoCode = !user?.hasFeatureFlag(APPLE_PROMO_CODE_FEATURE_FLAG);
  const usePromoCode =
    !!promoCode &&
    promoCodePrices?.[duration]?.recurring_price &&
    !hidePromoCode;
  const discount = getPromoCodeRoundedDiscount(
    plans,
    plan,
    duration,
    promoCodePrices
  );
  const isCurrentPlan =
    plan?.subscription_plan_type === user?.subscription_plan_type &&
    duration === user?.package_duration;

  const onPayClick = () => {
    if (!plan || !user) return;
    if (warningMessage) {
      openMessage(warningMessage, "warning");
      return;
    }
    if (
      getIsDifferentPaymentMethod(user) &&
      !cancelLastSubscriptionModalOpen &&
      !appleOfferCodeModalOpen
    ) {
      setCancelLastSubscriptionModalOpen(true);
      return;
    }
    if (
      IS_APPLE_PAY &&
      !hidePromoCode &&
      usePromoCode &&
      !appleOfferCodeModalOpen
    ) {
      setCancelLastSubscriptionModalOpen(false);
      setAppleOfferCodeModalOpen(true);
      return;
    }
    const productId = getProductId(
      getEnvironmentType(),
      plan.subscription_type,
      duration,
      undefined,
      IS_GOOGLE_PAY
    );
    if (!productId) return;
    setIsLoading(true);
    initiatePayment({
      plan,
      duration,
      promoCode: usePromoCode ? promoCode : undefined,
      discount: usePromoCode ? discount : undefined,
      userUuid: user?.user_uuid,
      productId,
      isUpgrade,
      isCurrentPlan,
      paymentMethod: IS_GOOGLE_PAY
        ? SubscriptionPaymentMethodType.GOOGLEPAY_SUBSCRIPTION
        : SubscriptionPaymentMethodType.APPLEPAY_SUBSCRIPTION,
    })
      .then((result) => {
        if (result?.isAlreadySubscribed) return;
        if (result?.isUserCancelled) {
          openMessage("Payment cancelled - no charge was made.", "warning");
        } else {
          history.push(
            `${BILLING_HISTORY_QUERY_PARAM}?${PROMO_CODE_QUERY_PARAM}=1`
          );
        }
      })
      .catch(() => {
        openMessage(
          "Something went wrong while initiating the payment request. Please try again or contact support.",
          "error"
        );
      })
      .finally(() => {
        setIsLoading(false);
        setAppleOfferCodeModalOpen(false);
        setCancelLastSubscriptionModalOpen(false);
      });
  };

  const payButtonData = {
    text: `Pay with ${IS_GOOGLE_PAY ? "Google Pay" : "Apple Pay"}`,
    onClick: onPayClick,
    error: !!warningMessage,
    loading,
  };

  const cancelLastSubscriptionModalProps = {
    open: cancelLastSubscriptionModalOpen,
    onClose: () => setCancelLastSubscriptionModalOpen(false),
    onPayClick,
    buttonLoading: loading,
  };

  const appleOfferCodeModalProps = {
    open: appleOfferCodeModalOpen,
    closeModal: () => setAppleOfferCodeModalOpen(false),
    onApplePayClick: onPayClick,
    buttonLoading: loading,
  };

  return {
    user,
    hidePromoCode,
    payButtonData,
    cancelLastSubscriptionModalProps,
    appleOfferCodeModalProps,
  };
};

export default useWebViewCheckout;
